import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Define initial state
const initialState = {
  webReportContent: {},
  requestStatus: "idle", // 'idle' | 'loading' | 'success' | 'fail',
  error: null,
  consentStatus: null, // null | true | false
  consentStatusRequestStatus: "idle", // 'idle' | 'loading' | 'success' | 'fail'
  consentUpdateRequestStatus: "idle", // 'idle' | 'loading' | 'success' | 'fail'
};

const webReportSlice = createSlice({
  name: "webReportV1",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchWebReport.pending, (state) => {
      state.requestStatus = "loading";
    });
    builder.addCase(fetchWebReport.fulfilled, (state, action) => {
      state.requestStatus = "success";
      state.webReportContent = action.payload.report_data;
    });
    builder.addCase(fetchWebReport.rejected, (state, action) => {
      state.requestStatus = "fail";
      state.error = action.payload;
    });

    // Fetch consent status reducers
    builder.addCase(fetchConsentStatus.pending, (state) => {
      state.consentStatusRequestStatus = "loading";
    });
    builder.addCase(fetchConsentStatus.fulfilled, (state, action) => {
      state.consentStatusRequestStatus = "success";
      state.consentStatus = action.payload.consented;
    });
    builder.addCase(fetchConsentStatus.rejected, (state) => {
      state.consentStatusRequestStatus = "fail";
      state.consentStatus = false; // Default to false on failure to ensure sheet shows
    });

    // Update consent status reducers
    builder.addCase(updateConsentStatus.pending, (state) => {
      state.consentUpdateRequestStatus = "loading";
    });
    builder.addCase(updateConsentStatus.fulfilled, (state, action) => {
      state.consentUpdateRequestStatus = "success";
      // Optionally update consentStatus based on the request
      state.consentStatus = action.meta.arg.consented;
    });
    builder.addCase(updateConsentStatus.rejected, (state) => {
      state.consentUpdateRequestStatus = "fail";
    });
  },
});

export const fetchWebReport = createAsyncThunk(
  "webReportV1/fetchWebReport",
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/compiled-reports/${reportId}/`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch web report");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchConsentStatus = createAsyncThunk(
  "webReportV1/fetchConsentStatus",
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/compiled-reports/${reportId}/consent/`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch consent status");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateConsentStatus = createAsyncThunk(
  "webReportV1/updateConsentStatus",
  async ({ reportId, consented }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/compiled-reports/${reportId}/consent/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ consented }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update consent status");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export default webReportSlice.reducer;
